import { useCallback, useEffect, useRef, useState } from "react";

export interface ServerTableParams {
  page: number;
  size: number;
  sort?: string;
  q?: string;
}

export interface ServerTablePage<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
}

export interface UseServerTableResult<T> {
  rows: T[];
  totalElements: number;
  totalPages: number;
  loading: boolean;
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  sortField?: string;
  sortDir?: "asc" | "desc";
  setSort: (field: string) => void;
  /** The effective sort string currently driving the grid — `sortField,sortDir` if the
   *  user picked a column, else the hook's default. Pass this straight through to an
   *  export/download call so it matches whatever order the grid is showing. */
  sort: string;
  q: string;
  setQ: (q: string) => void;
  refetch: () => void;
}

const SEARCH_DEBOUNCE_MS = 300;

/**
 * Drives a server-paginated, sortable, searchable table.
 *
 * `fetchPage` is called whenever page/pageSize/sort/q/deps change. Changing
 * `q`, sort, or pageSize resets `page` back to 0 (but changing `page` itself
 * does not reset `page`, obviously). The search box is debounced ~300ms
 * before it triggers a fetch. Defaults to newest-first (`updatedAt,desc`)
 * until the user clicks a column to sort by something else.
 */
export function useServerTable<T>(
  fetchPage: (params: ServerTableParams) => Promise<ServerTablePage<T>>,
  deps: unknown[] = [],
  defaultSort = "updatedAt,desc",
): UseServerTableResult<T> {
  const [rows, setRows] = useState<T[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sortField, setSortField] = useState<string | undefined>(undefined);
  const [sortDir, setSortDir] = useState<"asc" | "desc" | undefined>(undefined);

  const [q, setQInternal] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchPageRef = useRef(fetchPage);
  fetchPageRef.current = fetchPage;

  const setQ = useCallback((next: string) => {
    setQInternal(next);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedQ(next);
    }, SEARCH_DEBOUNCE_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const setSort = useCallback((field: string) => {
    if (sortField !== field) {
      setSortField(field);
      setSortDir("asc");
    } else if (sortDir === "asc") {
      setSortDir("desc");
    } else {
      setSortField(undefined);
      setSortDir(undefined);
    }
  }, [sortField, sortDir]);

  // Reset to first page when q / sort / pageSize change (not when page itself changes).
  const isFirstRun = useRef(true);
  useEffect(() => {
    if (isFirstRun.current) return;
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ, sortField, sortDir, pageSize]);

  const [refetchTick, setRefetchTick] = useState(0);
  const refetch = useCallback(() => setRefetchTick(t => t + 1), []);

  // Defaults to newest-first (by last create/update) until the user picks their own
  // column sort — otherwise newly added/edited rows land wherever the backend's
  // unspecified natural order puts them instead of appearing at the top. Callers with
  // a more meaningful natural order (e.g. units by unit number) can override this.
  const sort = sortField ? `${sortField},${sortDir ?? "asc"}` : defaultSort;

  useEffect(() => {
    isFirstRun.current = false;
    let cancelled = false;
    setLoading(true);
    fetchPageRef
      .current({ page, size: pageSize, sort, q: debouncedQ || undefined })
      .then(res => {
        if (cancelled) return;
        setRows(res.content);
        setTotalElements(res.totalElements);
        setTotalPages(res.totalPages);
      })
      .catch(() => {
        if (cancelled) return;
        setRows([]);
        setTotalElements(0);
        setTotalPages(0);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, sortField, sortDir, debouncedQ, refetchTick, ...deps]);

  return {
    rows,
    totalElements,
    totalPages,
    loading,
    page,
    setPage,
    pageSize,
    setPageSize,
    sortField,
    sortDir,
    setSort,
    sort,
    q,
    setQ,
    refetch,
  };
}
